import { createSdk } from '@commerce-blocks/sdk';

const LAYERS_TOKEN_PLACEHOLDER = 'YOUR_LAYERS_PUBLIC_TOKEN';
const DEFAULT_PAGE_SIZE = 60;
const SDK_STATE = {
  sdk: null,
  error: null,
};

function getLayersConfig() {
  return window.layersConfig || {};
}

function getPublicToken() {
  return getLayersConfig().publicToken || LAYERS_TOKEN_PLACEHOLDER;
}

function normalizeSorts(items) {
  if (!Array.isArray(items)) return [];
  const normalized = items
    .map((item) => ({
      code: item?.code,
      label: item?.name || item?.label,
      scope: item?.scope,
      order: item?.order,
    }))
    .filter((item) => item.code && item.label);

  return normalized.sort((a, b) => {
    const aOrder = Number.isFinite(Number(a.order)) ? Number(a.order) : Number.POSITIVE_INFINITY;
    const bOrder = Number.isFinite(Number(b.order)) ? Number(b.order) : Number.POSITIVE_INFINITY;
    if (aOrder !== bOrder) return aOrder - bOrder;
    return String(a.label).localeCompare(String(b.label));
  });
}

function normalizeFacets(items) {
  if (!Array.isArray(items)) return [];
  return items.map((item) => item?.code).filter(Boolean);
}

function getSdk() {
  if (SDK_STATE.sdk || SDK_STATE.error) return SDK_STATE.sdk;

  const token = getPublicToken();
  if (!token || token === LAYERS_TOKEN_PLACEHOLDER) {
    console.warn('[Layers] Public token is not configured.');
  }

  const config = getLayersConfig();
  const result = createSdk({
    layersPublicToken: token,
    sorts: normalizeSorts(config.sortOrders || []),
    facets: normalizeFacets(config.facets || []),
    transformFilters: normalizeFiltersForSdk,
    filterMap: config.filterMap || {},
    sortMap: config.sortMap || {},
    extendCollection: ({ base, raw }) => ({
      ...base,
      banners: raw?.banners || [],
      appliedRules: raw?.appliedRules || raw?.applied_rules || [],
    }),
  });

  if (result.error) {
    SDK_STATE.error = result.error;
    console.error('[Layers] SDK init failed', result.error.message);
    return null;
  }

  SDK_STATE.sdk = result.data;
  return SDK_STATE.sdk;
}

function isFilterGroup(value) {
  return Boolean(value && typeof value === 'object' && !Array.isArray(value) && Array.isArray(value.expressions));
}

function normalizeFiltersForSdk(filters) {
  if (!filters) return undefined;
  if (isFilterGroup(filters)) return filters;
  if (typeof filters !== 'object' || Array.isArray(filters)) return filters;

  const listFilters = {};
  const rangeFilters = {};
  let hasListFilters = false;
  let hasRangeFilters = false;

  Object.entries(filters).forEach(([key, value]) => {
    if (value == null) return;
    if (typeof value === 'object' && !Array.isArray(value)) {
      const min = Number(value.min);
      const max = Number(value.max);
      if (Number.isFinite(min) || Number.isFinite(max)) {
        rangeFilters[key] = {};
        if (Number.isFinite(min)) rangeFilters[key].min = min;
        if (Number.isFinite(max)) rangeFilters[key].max = max;
        hasRangeFilters = true;
        return;
      }
    }

    listFilters[key] = value;
    hasListFilters = true;
  });

  return buildFilterGroup(
    hasListFilters ? listFilters : undefined,
    hasRangeFilters ? rangeFilters : undefined
  );
}

function escapeHtml(value) {
  return String(value == null ? '' : value)
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');
}

function escapeAttr(value) {
  return escapeHtml(value).replace(/`/g, '&#96;');
}

function stripWrappingQuotes(value) {
  const normalized = String(value ?? '').trim();
  if (!normalized) return '';
  return normalized.replace(/^["']+|["']+$/g, '');
}

function normalizeId(value) {
  if (value == null) return '';
  const raw = String(value).trim();
  if (!raw) return '';
  if (!raw.includes('gid://')) return raw;
  const parts = raw.split('/');
  return parts[parts.length - 1] || raw;
}

function handleize(value) {
  return String(value || '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');
}

function getUrlParams() {
  return new URLSearchParams(window.location.search);
}

function isFilterParam(key) {
  return key.startsWith('filters[');
}

function parseFiltersFromParams(params = getUrlParams()) {
  const listFilters = {};
  const rangeFilters = {};
  let hasListFilters = false;
  let hasRangeFilters = false;

  params.forEach((value, key) => {
    const rangeMatch = key.match(/^filters\[(.+)\]\[(min|max)\]$/);
    if (rangeMatch) {
      const facetKey = rangeMatch[1];
      const bound = rangeMatch[2];
      const numeric = Number(value);
      if (!facetKey || !Number.isFinite(numeric)) return;
      if (!rangeFilters[facetKey]) rangeFilters[facetKey] = {};
      rangeFilters[facetKey][bound] = numeric;
      hasRangeFilters = true;
      return;
    }

    const listMatch = key.match(/^filters\[(.+)\]$/);
    if (!listMatch) return;
    const facetKey = listMatch[1];
    const normalizedValue = stripWrappingQuotes(value);
    if (!facetKey || normalizedValue === '') return;
    hasListFilters = true;

    if (listFilters[facetKey]) {
      listFilters[facetKey] = Array.isArray(listFilters[facetKey])
        ? [...listFilters[facetKey], normalizedValue]
        : [listFilters[facetKey], normalizedValue];
    } else {
      listFilters[facetKey] = normalizedValue;
    }
  });

  return {
    listFilters: hasListFilters ? listFilters : undefined,
    rangeFilters: hasRangeFilters ? rangeFilters : undefined,
  };
}

function buildFilterGroup(listFilters, rangeFilters) {
  if (!listFilters && !rangeFilters) return undefined;

  const config = getLayersConfig();
  const filterMap = config.filterMap || {};
  const expressions = [];

  if (listFilters) {
    Object.entries(listFilters).forEach(([key, rawValue]) => {
      if (rawValue == null) return;
      const mapEntry = filterMap[key];
      const property = typeof mapEntry === 'string' ? mapEntry : mapEntry?.property || key;
      const valueMap = typeof mapEntry === 'object' ? mapEntry.values : undefined;
      const values = (Array.isArray(rawValue) ? rawValue : [rawValue]).map((value) => {
        const normalized = String(value);
        return valueMap?.[normalized] ?? normalized;
      });
      if (!values.length) return;
      expressions.push({
        property,
        operator: values.length > 1 ? 'in' : 'eq',
        values,
      });
    });
  }

  if (rangeFilters) {
    Object.entries(rangeFilters).forEach(([key, range]) => {
      if (!range) return;
      const mapEntry = filterMap[key];
      const property = typeof mapEntry === 'string' ? mapEntry : mapEntry?.property || key;
      const min = Number(range.min);
      const max = Number(range.max);
      if (Number.isFinite(min)) expressions.push({ property, operator: 'gte', values: [min] });
      if (Number.isFinite(max)) expressions.push({ property, operator: 'lte', values: [max] });
    });
  }

  if (!expressions.length) return undefined;
  return { conditional: 'AND', expressions };
}

function getActiveFacetKeys(listFilters, rangeFilters) {
  return [...new Set([...Object.keys(listFilters || {}), ...Object.keys(rangeFilters || {})])];
}

function omitFacetFromFilters(listFilters, rangeFilters, facetKey) {
  const nextListFilters = {};
  const nextRangeFilters = {};

  Object.entries(listFilters || {}).forEach(([key, value]) => {
    if (key !== facetKey) nextListFilters[key] = value;
  });

  Object.entries(rangeFilters || {}).forEach(([key, value]) => {
    if (key !== facetKey) nextRangeFilters[key] = value;
  });

  return {
    listFilters: Object.keys(nextListFilters).length ? nextListFilters : undefined,
    rangeFilters: Object.keys(nextRangeFilters).length ? nextRangeFilters : undefined,
  };
}

function mergeFacetData(baseFacets, baseRanges, disjunctiveFacets) {
  const facets = { ...(baseFacets || {}) };
  const facetRanges = { ...(baseRanges || {}) };

  Object.entries(disjunctiveFacets || {}).forEach(([key, data]) => {
    if (data?.values) facets[key] = data.values;
    if (data?.range) facetRanges[key] = data.range;
  });

  return { facets, facetRanges };
}

function getSortFromParams(params = getUrlParams()) {
  const config = getLayersConfig();
  const raw = params.get('sort') || params.get('sort_by') || '';
  if (!raw) return '';
  return config.sortMap?.[raw] || raw;
}

function getPageFromParams(params = getUrlParams()) {
  const page = Number(params.get('page'));
  return Number.isFinite(page) && page > 0 ? page : 1;
}

function setHistoryParams(params, replace = false) {
  const query = params.toString();
  const nextUrl = `${window.location.pathname}${query ? `?${query}` : ''}`;
  if (replace) {
    history.replaceState({ layers: true }, '', nextUrl);
  } else {
    history.pushState({ layers: true }, '', nextUrl);
  }
}

function getTotalResults(data, fallback = 0) {
  const total = data?.total ?? data?.totalResults ?? data?.total_results;
  return Number.isFinite(Number(total)) ? Number(total) : fallback;
}

function getTotalPages(data, limit, fallback = 1) {
  const totalPages = data?.totalPages ?? data?.total_pages;
  if (Number.isFinite(Number(totalPages))) return Number(totalPages);
  const totalResults = getTotalResults(data);
  if (Number.isFinite(Number(totalResults)) && Number.isFinite(Number(limit)) && Number(limit) > 0) {
    return Math.max(1, Math.ceil(Number(totalResults) / Number(limit)));
  }
  return fallback;
}

function filterSortsByScope(sorts, scopeTokens) {
  const tokens = Array.isArray(scopeTokens) ? scopeTokens : [scopeTokens];
  const normalizedTokens = tokens.filter(Boolean).map((token) => String(token).toLowerCase());
  if (!normalizedTokens.length) return sorts;
  return sorts.filter((item) => {
    const scope = item?.scope;
    if (!scope) return false;
    const values = Array.isArray(scope) ? scope : [scope];
    const scopeSet = values.map((value) => String(value).toLowerCase());
    return normalizedTokens.some((token) => scopeSet.includes(token));
  });
}

function getFacetLabelMap() {
  const facets = getLayersConfig().facets || [];
  return facets.reduce((acc, facet) => {
    if (facet?.code) acc[facet.code] = facet?.name || facet.code;
    return acc;
  }, {});
}

const COLOR_SWATCH_BY_NAME = Object.freeze({
  beige: '#F2E8E2',
  black: '#000000',
  blue: '#0088FF',
  brown: '#AC7F5E',
  burgundy: '#6D1A36',
  clear: 'transparent',
  gold: '#D4AF37',
  green: '#34C759',
  grey: '#8A8A8A',
  gray: '#8A8A8A',
  khaki: '#b8a46a',
  multi: 'linear-gradient(135deg, #FF383C 0%, #FF9500 20%, #FFD60A 40%, #34C759 60%, #0088FF 80%, #AF52DE 100%)',
  orange: '#FF9500',
  pink: '#FFB8F2',
  purple: '#AF52DE',
  red: '#FF383C',
  rosegold: '#B76E79',
  silver: '#C0C0C0',
  white: '#FFFFFF',
  yellow: '#FFD60A',
});

const COLOR_SWATCH_BY_CODE = Object.freeze({
  bei: 'beige',
  bge: 'beige',
  blk: 'black',
  blc: 'black',
  blu: 'blue',
  brn: 'brown',
  bur: 'burgundy',
  clr: 'clear',
  gld: 'gold',
  grn: 'green',
  gry: 'grey',
  khk: 'khaki',
  mlt: 'multi',
  org: 'orange',
  pnk: 'pink',
  ppl: 'purple',
  pur: 'purple',
  red: 'red',
  rsg: 'rosegold',
  rose: 'rosegold',
  slv: 'silver',
  wht: 'white',
  yel: 'yellow',
  ylw: 'yellow',
});

function isColorFacetKey(facetKey, title) {
  const key = String(facetKey || '').toLowerCase();
  const label = String(title || '').toLowerCase();
  return (
    /\bcolou?r\b/.test(label) ||
    /\bcolou?r\b/.test(key) ||
    key.endsWith('.color') ||
    key.endsWith('_color') ||
    key.includes('color') ||
    key.includes('colour')
  );
}

function isSizeFacetKey(facetKey, title) {
  const key = String(facetKey || '').toLowerCase();
  const label = String(title || '').toLowerCase();
  return (
    /\bsizes?\b/.test(label) ||
    /\bsizes?\b/.test(key) ||
    key.endsWith('.size') ||
    key.endsWith('_size') ||
    key.includes('size')
  );
}

function tokenizeFacetValue(value) {
  return String(value || '')
    .toLowerCase()
    .split(/[^a-z0-9#]+/)
    .map((token) => token.trim())
    .filter(Boolean);
}

function getColorSwatchBackground(rawValue) {
  const value = String(rawValue || '').trim();
  if (!value) return '';
  if (/^#(?:[0-9a-f]{3}|[0-9a-f]{6})$/i.test(value)) return value;
  if (/^rgba?\(/i.test(value)) return value;

  const normalized = value.toLowerCase();
  const compact = normalized.replace(/[^a-z0-9]/g, '');

  if (COLOR_SWATCH_BY_NAME[compact]) return COLOR_SWATCH_BY_NAME[compact];
  if (COLOR_SWATCH_BY_CODE[compact]) return COLOR_SWATCH_BY_NAME[COLOR_SWATCH_BY_CODE[compact]];

  const tokens = tokenizeFacetValue(value);
  for (const token of tokens) {
    if (COLOR_SWATCH_BY_NAME[token]) return COLOR_SWATCH_BY_NAME[token];
    if (COLOR_SWATCH_BY_CODE[token]) return COLOR_SWATCH_BY_NAME[COLOR_SWATCH_BY_CODE[token]];
  }

  return '';
}

function parseFacetRange(range) {
  if (!range || typeof range !== 'object') return null;
  const minRaw = range.min ?? range.min_value ?? range.minValue ?? range.from ?? range.low ?? range.start;
  const maxRaw = range.max ?? range.max_value ?? range.maxValue ?? range.to ?? range.high ?? range.end;
  const min = Number(minRaw?.amount ?? minRaw);
  const max = Number(maxRaw?.amount ?? maxRaw);
  if (!Number.isFinite(min) || !Number.isFinite(max)) return null;
  return { min, max };
}

const SIZE_VALUE_ORDER = Object.freeze([
  'xxs',
  'xs',
  's',
  'm',
  'l',
  'xl',
  'xxl',
  'xxxl',
  'xxxxl',
  '4xl',
  '5xl',
  '6xl',
]);

function getSizeValueSortRank(value) {
  const normalized = String(value || '')
    .toLowerCase()
    .replace(/\s+/g, '')
    .replace(/-/g, '');

  const directIndex = SIZE_VALUE_ORDER.indexOf(normalized);
  if (directIndex >= 0) return directIndex;

  if (normalized.includes('onesize') || normalized === 'os' || normalized === 'o/s') {
    return SIZE_VALUE_ORDER.length + 1;
  }

  const match = normalized.match(/^(\d+)(?:us|uk|eu)?$/);
  if (match) return SIZE_VALUE_ORDER.length + Number(match[1]);

  return Number.POSITIVE_INFINITY;
}

function getSelectedFacetValues(params = getUrlParams()) {
  const map = new Map();
  params.forEach((value, key) => {
    const match = key.match(/^filters\[(.+)\]$/);
    if (!match) return;
    const facetKey = match[1];
    const normalizedValue = stripWrappingQuotes(value);
    if (!facetKey || normalizedValue === '') return;
    if (!map.has(facetKey)) map.set(facetKey, new Set());
    map.get(facetKey).add(normalizedValue);
  });
  return map;
}

function getSelectedRangeValues(params = getUrlParams()) {
  const map = new Map();
  params.forEach((value, key) => {
    const match = key.match(/^filters\[(.+)\]\[(min|max)\]$/);
    if (!match) return;
    const facetKey = match[1];
    const bound = match[2];
    const numeric = Number(value);
    if (!Number.isFinite(numeric)) return;
    if (!map.has(facetKey)) map.set(facetKey, {});
    map.get(facetKey)[bound] = numeric;
  });
  return map;
}

function formatPrice(product) {
  const minFormatted = product?.priceRange?.min?.formatted || '';
  const maxFormatted = product?.priceRange?.max?.formatted || '';
  const min = Number(product?.priceRange?.min?.amount);
  const max = Number(product?.priceRange?.max?.amount);
  if (Number.isFinite(min) && Number.isFinite(max) && min !== max) {
    return `From ${minFormatted || maxFormatted || ''}`.trim();
  }
  return minFormatted || maxFormatted || '';
}

function formatCompareAtPrice(product) {
  return product?.compareAtPriceRange?.min?.formatted || '';
}

function isOnSale(product) {
  const compare = Number(product?.compareAtPriceRange?.min?.amount);
  const price = Number(product?.priceRange?.min?.amount);
  return Number.isFinite(compare) && Number.isFinite(price) && compare > price;
}

const CARD_IMAGE_SRCSET_SIZES = [360, 480, 600, 720, 840, 960, 1200, 1440];
const CARD_IMAGE_SIZES = '(min-width: 1440px) 360px, (min-width: 1200px) 25vw, (min-width: 750px) 33vw, 50vw';

function addImageSizeParametersToUrl({ src, size }) {
  try {
    const url = new URL(src);
    if (size) url.searchParams.set('width', `${size}`);
    return url.toString();
  } catch (error) {
    return src;
  }
}

function rewriteShopifyFileImageUrl(src) {
  const fileNamePart = String(src || '').split('/').pop();
  if (!fileNamePart) return src;
  return `${window.location.protocol}//${window.location.hostname}/cdn/shop/files/${fileNamePart}`;
}

function generateSrcSet({ src, srcSetSizes = CARD_IMAGE_SRCSET_SIZES }) {
  if (!src || !srcSetSizes?.length) return '';

  return srcSetSizes
    .map(
      (size) =>
        `${addImageSizeParametersToUrl({
          src,
          size,
        })} ${size}w`
    )
    .join(', ');
}

function renderCardImage({ src, alt, className = '' }) {
  const imageUrl = rewriteShopifyFileImageUrl(src);
  const srcSet = generateSrcSet({ src: imageUrl });
  const classAttr = className ? ` class="${escapeAttr(className)}"` : '';

  return `<img src="${escapeAttr(addImageSizeParametersToUrl({ src: imageUrl, size: CARD_IMAGE_SRCSET_SIZES[0] }))}" srcset="${escapeAttr(srcSet)}" sizes="${escapeAttr(CARD_IMAGE_SIZES)}" alt="${escapeAttr(alt)}" loading="lazy"${classAttr}>`;
}

function renderGenericProductCard(product) {
  const url = product?.onlineStoreUrl || `/products/${product?.handle || ''}`;
  const visibleImages = (product?.images || []).filter(
    (img) => !img?.altText?.includes('_404') && !img?.url?.includes('_404')
  );
  const image = visibleImages[0] || null;
  const hoverImage = visibleImages[1] || null;
  const title = product?.title || '';
  const price = formatPrice(product);
  const compare = formatCompareAtPrice(product);
  const onSale = isOnSale(product);
  const soldOut = product?.availableForSale === false;
  const enableHoverImage = getLayersConfig().productCardMediaHover === true;
  const hasHoverImage =
    enableHoverImage &&
    Boolean(image?.url) &&
    Boolean(hoverImage?.url) &&
    image.url !== hoverImage.url;
  const mediaClass = hasHoverImage
    ? 'media product-card__media product-card__media--hoverable'
    : 'media product-card__media';

  return `
    <product-card class="product-card${soldOut ? ' product-card__sold-out' : ''}">
      <div class="product-card__actions">
        <a href="${escapeAttr(url)}" tabindex="-1" class="product-card__link" data-product-link title="${escapeAttr(title)}">
          <div class="${mediaClass}">
            ${
              image?.url
                ? renderCardImage({ src: image.url, alt: title })
                : '<span class="placeholder-svg"></span>'
            }
            ${
              hasHoverImage
                ? renderCardImage({ src: hoverImage.url, alt: title, className: 'hover-img' })
                : ''
            }
          </div>
        </a>
      </div>
      <div class="product-card__content">
        <div class="product-card__info">
          <h3 class="product-card__title">
            <a href="${escapeAttr(url)}" class="product-card__link" data-product-link title="${escapeAttr(title)}">
              <span>${escapeHtml(title)}</span>
            </a>
          </h3>
          <div class="product-card__price">
            <div class="price${onSale ? ' price--on-sale' : ''}">
              <div class="price__container">
                ${onSale && compare
                  ? `<div class="price__sale"><div class="price__sale-inner"><s>${escapeHtml(compare)}</s><ins>${escapeHtml(price)}</ins></div></div>`
                  : `<div class="price__regular"><span>${escapeHtml(price)}</span></div>`
                }
              </div>
            </div>
          </div>
        </div>
      </div>
    </product-card>
  `;
}

function buildPaginationItems(currentPage, totalPages) {
  if (totalPages <= 7) return Array.from({ length: totalPages }, (_, index) => index + 1);

  let start = Math.max(2, currentPage - 2);
  let end = Math.min(totalPages - 1, currentPage + 2);

  if (currentPage <= 3) {
    start = 2;
    end = Math.min(totalPages - 1, 5);
  }
  if (currentPage >= totalPages - 2) {
    end = totalPages - 1;
    start = Math.max(2, totalPages - 4);
  }

  const items = [1];
  if (start > 2) items.push('ellipsis');
  for (let page = start; page <= end; page += 1) items.push(page);
  if (end < totalPages - 1) items.push('ellipsis');
  items.push(totalPages);
  return items;
}

function renderPagination(container, currentPage, totalPages) {
  if (!container) return;
  if (!Number.isFinite(Number(totalPages)) || totalPages <= 1) {
    container.innerHTML = '';
    return;
  }

  const safeCurrent = Math.min(Math.max(1, Number(currentPage) || 1), Number(totalPages));
  const items = buildPaginationItems(safeCurrent, totalPages);
  const listItems = [];

  if (safeCurrent > 1) {
    listItems.push(
      `<li><a class="pagination__prev-next" href="#" data-page="${safeCurrent - 1}" aria-label="Previous page" title="Previous page">&lsaquo;</a></li>`
    );
  }

  items.forEach((item) => {
    if (item === 'ellipsis') {
      listItems.push('<li><span aria-hidden="true">...</span></li>');
      return;
    }
    if (item === safeCurrent) {
      listItems.push(
        `<li><span class="pagination__current" aria-current="page" aria-label="Page ${item}">${item}</span></li>`
      );
      return;
    }
    listItems.push(
      `<li><a href="#" data-page="${item}" aria-label="Page ${item}" title="Page ${item}">${item}</a></li>`
    );
  });

  if (safeCurrent < totalPages) {
    listItems.push(
      `<li><a class="pagination__prev-next" href="#" data-page="${safeCurrent + 1}" aria-label="Next page" title="Next page">&rsaquo;</a></li>`
    );
  }

  container.innerHTML = `
    <nav class="pagination" aria-label="Pagination">
      <ul class="list-unstyled">
        ${listItems.join('')}
      </ul>
    </nav>
  `;
}

function buildFacetDetailsMarkup({
  facetKey,
  title,
  values,
  selectedValues,
  selectedRange,
  range,
  index,
  idPrefix,
  isColorFacet,
  isSizeFacet,
}) {
  const detailId = `${idPrefix}-${handleize(facetKey || title)}-${index}`;
  const hasActiveValues = selectedValues && selectedValues.size > 0;
  const hasActiveRange = selectedRange && (selectedRange.min != null || selectedRange.max != null);
  const isExpanded = hasActiveValues || hasActiveRange;
  const expandedClass = isExpanded ? ' is-active' : '';
  const openAttr = isExpanded ? ' open' : '';
  const expandedStyle = isExpanded ? ' style="height: auto;"' : '';

  if (range && range.min !== range.max) {
    const minValue = selectedRange?.min;
    const maxValue = selectedRange?.max;
    return `
      <details class="accordion__section facets__accordion-section${expandedClass}" id="${escapeAttr(detailId)}"${openAttr}>
        <summary class="facets__accordion-btn h4 js-btn">
          ${escapeHtml(title)}
          <div class="facets__accordion-icon"><span class="icon">⌄</span></div>
        </summary>
        <div class="accordion__body facets__accordion-body slim-scrollbar"${expandedStyle}>
          <div class="js-filter">
            <price-range class="facets__price">
              <div class="facets__price-row">
                <span>$</span>
                <div class="facets__price-field field">
                  <input class="field__input" type="number" name="filters[${escapeAttr(facetKey)}][min]" value="${minValue != null ? escapeAttr(minValue) : ''}" min="${escapeAttr(Math.floor(range.min))}" max="${escapeAttr(Math.ceil(range.max))}">
                  <label class="field__label">${escapeHtml('From')}</label>
                </div>
              </div>
              <div class="facets__price-row">
                <span>$</span>
                <div class="facets__price-field field">
                  <input class="field__input" type="number" name="filters[${escapeAttr(facetKey)}][max]" value="${maxValue != null ? escapeAttr(maxValue) : ''}" min="${escapeAttr(Math.floor(range.min))}" max="${escapeAttr(Math.ceil(range.max))}">
                  <label class="field__label">${escapeHtml('To')}</label>
                </div>
              </div>
            </price-range>
          </div>
        </div>
      </details>
    `;
  }

  const entries = Object.entries(values || {});
  if (!entries.length) return '';

  let sortedEntries = entries;
  if (isSizeFacet) {
    sortedEntries = [...entries].sort((a, b) => {
      const aRank = getSizeValueSortRank(a[0]);
      const bRank = getSizeValueSortRank(b[0]);
      if (aRank !== bRank) return aRank - bRank;
      return String(a[0]).localeCompare(String(b[0]));
    });
  } else {
    sortedEntries = [...entries].sort((a, b) => String(a[0]).localeCompare(String(b[0])));
  }

  const items = sortedEntries
    .map(([rawValue, count], valueIndex) => {
      const value = stripWrappingQuotes(rawValue);
      if (!value) return '';
      const checked = selectedValues?.has(value) ? ' checked' : '';
      const disabled = Number(count) <= 0 && !selectedValues?.has(value) ? ' disabled' : '';
      const itemClass = selectedValues?.has(value) ? ' facets__item facets__item--checked' : ' facets__item';
      const inputId = `${detailId}-${valueIndex}`;

      if (isSizeFacet) {
        return `
          <li class="${itemClass.trim()} layers-facet-size-item">
            <input
              type="checkbox"
              name="filters[${escapeAttr(facetKey)}]"
              value="${escapeAttr(value)}"
              id="${escapeAttr(inputId)}"
              class="product-option__input visually-hidden layers-facet-input layers-facet-input--size"
              ${checked}${disabled}
            >
            <label for="${escapeAttr(inputId)}" class="product-option__label product-option__label--square layers-facet-size-label">
              <span>${escapeHtml(value)}</span>
            </label>
          </li>
        `;
      }

      if (isColorFacet) {
        const swatchBackground = getColorSwatchBackground(value);
        const swatchMarkup = `<span class="layers-facet-swatch layers-facet-swatch--square" aria-hidden="true" style="--layers-swatch-background:${escapeAttr(
          swatchBackground || '#ffffff'
        )};"></span>`;
        return `
          <li class="${itemClass.trim()} layers-facet-color-item">
            <input
              type="checkbox"
              name="filters[${escapeAttr(facetKey)}]"
              value="${escapeAttr(value)}"
              id="${escapeAttr(inputId)}"
              class="visually-hidden layers-facet-input layers-facet-input--color"
              ${checked}${disabled}
            >
            <label for="${escapeAttr(inputId)}" class="layers-facet-label layers-facet-label--color-row">
              ${swatchMarkup}
              <span>${escapeHtml(value)}</span>
            </label>
          </li>
        `;
      }

      const swatchBackground = isColorFacet ? getColorSwatchBackground(value) : '';
      const swatchMarkup = swatchBackground
        ? `<span class="layers-facet-swatch" aria-hidden="true" style="--layers-swatch-background:${escapeAttr(swatchBackground)};"></span>`
        : '';
      const labelClass = swatchMarkup ? ' class="layers-facet-label layers-facet-label--color"' : '';
      return `
        <li class="${itemClass.trim()}">
          <div class="checkbox facet-checkbox" tabindex="0">
            <input type="checkbox" name="filters[${escapeAttr(facetKey)}]" value="${escapeAttr(value)}" id="${escapeAttr(inputId)}"${checked}${disabled}>
            <label for="${escapeAttr(inputId)}"${labelClass}>${swatchMarkup}<span>${escapeHtml(value)}</span></label>
          </div>
        </li>
      `;
    })
    .join('');

  return `
    <details class="accordion__section facets__accordion-section${expandedClass}" id="${escapeAttr(detailId)}"${openAttr}>
      <summary class="facets__accordion-btn h4 js-btn">
        ${escapeHtml(title)}
        <div class="facets__accordion-icon"><span class="icon">⌄</span></div>
      </summary>
      <div class="accordion__body facets__accordion-body slim-scrollbar"${expandedStyle}>
        <div class="js-filter">
          <ul class="facets__list list-unstyled${isSizeFacet ? ' layers-facet-list--size' : ''}${isColorFacet ? ' layers-facet-list--color' : ''}" role="list">
            ${items}
          </ul>
        </div>
      </div>
    </details>
  `;
}

function buildActiveFacetsMarkup({ listSelections, rangeSelections, facetLabelMap }) {
  const chips = [];

  listSelections.forEach((values, key) => {
    values.forEach((value) => {
      chips.push(`
        <div class="active-facets__button">
          <div class="active-facets__button-text">
            <span>${escapeHtml(facetLabelMap[key] || key)}: ${escapeHtml(value)}</span>
          </div>
          <a href="#" class="active-facets__button-link js-facet-remove" data-layers-remove-filter data-filter-key="${escapeAttr(key)}" data-filter-value="${escapeAttr(value)}">
            <div class="svg-wrapper">×</div>
          </a>
        </div>
      `);
    });
  });

  rangeSelections.forEach((range, key) => {
    const hasMin = Number.isFinite(Number(range.min));
    const hasMax = Number.isFinite(Number(range.max));
    if (!hasMin && !hasMax) return;
    const min = hasMin ? range.min : '0';
    const max = hasMax ? range.max : '∞';
    chips.push(`
      <div class="active-facets__button">
        <div class="active-facets__button-text">
          <span>${escapeHtml(facetLabelMap[key] || key)}: ${escapeHtml(min)} - ${escapeHtml(max)}</span>
        </div>
        <a href="#" class="active-facets__button-link js-facet-remove" data-layers-remove-range data-filter-key="${escapeAttr(key)}">
          <div class="svg-wrapper">×</div>
        </a>
      </div>
    `);
  });

  if (!chips.length) return '';

  chips.push(`
    <div class="active-facets__button active-facets__vertical-filter">
      <a href="#" class="active-facets__button-remove active-facets__button-text facets__actions-button focus-inset" data-layers-clear-filters>
        <span>Clear all</span>
      </a>
    </div>
  `);

  return chips.join('');
}

function extractPromoPlan(root) {
  const promoRoot = root.querySelector('[data-layers-promos]');
  if (!promoRoot) return [];
  return Array.from(promoRoot.children)
    .map((child) => ({
      index: Number(child.getAttribute('data-layers-promo-index')),
      html: child.innerHTML,
    }))
    .filter((item) => Number.isFinite(item.index) && item.html);
}

function injectPromos(cards, promos) {
  if (!promos?.length) return cards;
  const next = [...cards];
  promos.forEach((promo, offset) => {
    const insertAt = Math.max(0, Math.min(promo.index + offset, next.length));
    next.splice(insertAt, 0, promo.html);
  });
  return next;
}

const BANNER_MOBILE_MEDIA_QUERY = '(max-width: 749px)';

function collectBanners(bannersOrAppliedRules) {
  if (!Array.isArray(bannersOrAppliedRules)) return [];

  return bannersOrAppliedRules
    .flatMap((item) => (Array.isArray(item?.banners) ? item.banners : item))
    .filter(Boolean)
    .sort((a, b) => {
      const aIndex = Number.isFinite(Number(a?.sort_index)) ? Number(a.sort_index) : Number.POSITIVE_INFINITY;
      const bIndex = Number.isFinite(Number(b?.sort_index)) ? Number(b.sort_index) : Number.POSITIVE_INFINITY;
      return aIndex - bIndex;
    });
}

function getBannerLayoutForDevice(banner, isMobile) {
  const media = isMobile ? banner?.mobile_media : banner?.web_media;
  const layout = isMobile ? banner?.mobile_layout : banner?.web_layout;
  if (!media?.src || !layout) return null;

  return {
    id: banner?.id,
    mode: banner?.mode,
    link: banner?.link,
    media,
    layout,
  };
}

function buildGridCells({ products, banners, isMobile }) {
  const cells = (products || []).map((product) => ({
    type: 'product',
    html: renderGenericProductCard(product),
  }));

  const inlineBanners = collectBanners(banners)
    .map((banner) => ({
      banner,
      picked: getBannerLayoutForDevice(banner, isMobile),
    }))
    .filter(({ picked }) => picked?.layout?.placement === 'inline')
    .sort((a, b) => {
      const aIndex = Number.isFinite(Number(a.banner?.sort_index)) ? Number(a.banner.sort_index) : Number.POSITIVE_INFINITY;
      const bIndex = Number.isFinite(Number(b.banner?.sort_index)) ? Number(b.banner.sort_index) : Number.POSITIVE_INFINITY;
      return aIndex - bIndex;
    });

  inlineBanners.forEach(({ picked }) => {
    const position = Number.isFinite(Number(picked.layout.position)) ? Number(picked.layout.position) : 0;
    const cell = {
      type: 'banner',
      html: renderBannerCell(picked, 'inline'),
    };

    if (picked.mode === 'overtake') {
      if (position < cells.length) {
        cells[position] = cell;
      } else {
        cells.push(cell);
      }
      return;
    }

    cells.splice(Math.max(0, Math.min(position, cells.length)), 0, cell);
  });

  return cells.map((cell) => cell.html);
}

function renderBannerCell(picked, placement) {
  const width = Math.max(1, Number(picked?.layout?.width) || 1);
  const height = Math.max(1, Number(picked?.layout?.height) || 1);
  const mode = picked?.mode === 'overtake' ? 'overtake' : 'inject';
  const classes = `layers-banner layers-banner--${placement} layers-banner--${mode}`;
  const attrs = `class="${escapeAttr(classes)}" data-banner-id="${escapeAttr(picked?.id || '')}" style="--span-x:${escapeAttr(width)};--span-y:${escapeAttr(height)}"`;
  const image = `<img src="${escapeAttr(picked?.media?.src)}" alt="${escapeAttr(picked?.media?.alt || '')}" loading="lazy">`;

  if (mode === 'inject' && picked?.link) {
    return `<a href="${escapeAttr(picked.link)}" ${attrs}>${image}</a>`;
  }

  return `<div ${attrs}>${image}</div>`;
}

class LayersCollectionResults extends HTMLElement {
  connectedCallback() {
    this.sdk = getSdk();
    this.grid = this.querySelector('[data-layers-grid]');
    if (!this.sdk || !this.grid) return;

    this.handle = this.dataset.collectionHandle;
    this.limit = Number(this.dataset.limit) || DEFAULT_PAGE_SIZE;
    this.columns = Number(this.dataset.columns) || 4;
    this.columnsMobile = Number(this.dataset.columnsMobile) || 2;
    this.defaultSort = String(this.dataset.defaultSort || '').trim();
    this.filtersMode = this.dataset.filters || 'drawer';
    this.section = this.closest('.shopify-section') || document;
    this.sidebarPanel = this.section.querySelector('[data-layers-sidebar-panel]');
    this.sidebarOpenButtons = Array.from(this.section.querySelectorAll('[data-layers-sidebar-open]'));
    this.sidebarCloseButton = this.section.querySelector('[data-layers-sidebar-close]');
    this.paginationContainer = this.querySelector('[data-layers-pagination]');
    this.heroBannerContainer = this.querySelector('[data-layers-banner-heroes]');
    this.promos = extractPromoPlan(this);
    this.gridSkeletonMarkup = this.grid.innerHTML;
    this.filterSkeletonByContainer = new WeakMap();
    this.section
      .querySelectorAll('[data-layers-drawer-filters], [data-layers-sidebar-filters]')
      .forEach((container) => {
        this.filterSkeletonByContainer.set(container, container.innerHTML);
      });
    this.hasLoadedResults = false;
    this.requestToken = 0;
    this.controller = this.sdk.collection({
      handle: this.handle,
      defaultSort: this.defaultSort || 'best_selling',
    });

    this.onPopState = () => this.refresh();
    this.bannerMediaQuery = window.matchMedia(BANNER_MOBILE_MEDIA_QUERY);
    this.onBannerMediaChange = () => this.refresh();
    window.addEventListener('popstate', this.onPopState);
    this.bannerMediaQuery.addEventListener?.('change', this.onBannerMediaChange);

    this.bindSidebarControls();
    this.bindEvents();
    this.refresh();
  }

  disconnectedCallback() {
    window.removeEventListener('popstate', this.onPopState);
    this.bannerMediaQuery?.removeEventListener?.('change', this.onBannerMediaChange);
    this.controller?.dispose?.();
  }

  bindEvents() {
    this.section.addEventListener('submit', (event) => {
      const form = event.target.closest('[data-layers-filters-form]');
      if (!form) return;
      event.preventDefault();
      this.applyFormToUrl({ resetPage: true });
    });

    this.section.addEventListener('change', (event) => {
      const target = event.target;
      if (!target) return;
      const inFiltersForm = target.closest('[data-layers-filters-form]');
      if (!inFiltersForm) return;
      event.preventDefault();
      this.applyFormToUrl({ resetPage: true });
    });

    this.section.addEventListener('click', (event) => {
      const sidebarOpenButton = event.target.closest('[data-layers-sidebar-open]');
      if (sidebarOpenButton) {
        event.preventDefault();
        this.setSidebarCollapsed(false);
        return;
      }

      const sidebarCloseButton = event.target.closest('[data-layers-sidebar-close]');
      if (sidebarCloseButton) {
        event.preventDefault();
        this.setSidebarCollapsed(true);
        return;
      }

      const applyButton = event.target.closest('[data-apply-filters]');
      if (applyButton) {
        event.preventDefault();
        event.stopPropagation();
        this.applyFormToUrl({ resetPage: true });
        const drawer = applyButton.closest('menu-drawer');
        if (typeof drawer?.closeDrawer === 'function') {
          drawer.closeDrawer();
        } else {
          drawer?.toggleDrawer();
        }
        return;
      }

      const clearButton = event.target.closest('[data-layers-clear-filters]');
      if (clearButton) {
        event.preventDefault();
        this.clearFilters();
        return;
      }

      const removeFilterButton = event.target.closest('[data-layers-remove-filter]');
      if (removeFilterButton) {
        event.preventDefault();
        const key = removeFilterButton.getAttribute('data-filter-key');
        const value = removeFilterButton.getAttribute('data-filter-value');
        this.removeSingleFilterValue(key, value);
        return;
      }

      const removeRangeButton = event.target.closest('[data-layers-remove-range]');
      if (removeRangeButton) {
        event.preventDefault();
        const key = removeRangeButton.getAttribute('data-filter-key');
        this.removeRangeFilter(key);
        return;
      }

      const pageLink = event.target.closest('[data-page]');
      if (pageLink && this.paginationContainer?.contains(pageLink)) {
        event.preventDefault();
        const page = Number(pageLink.getAttribute('data-page'));
        if (!Number.isFinite(page) || page < 1) return;
        this.setPage(page);
      }
    });
  }

  bindSidebarControls() {
    if (this.filtersMode !== 'sidebar') return;

    this.sidebarOpenButtons.forEach((button) => {
      if (button.dataset.layersSidebarBound === 'true') return;
      button.dataset.layersSidebarBound = 'true';
      button.addEventListener('click', (event) => {
        event.preventDefault();
        this.setSidebarCollapsed(false);
      });
    });

    if (this.sidebarCloseButton && this.sidebarCloseButton.dataset.layersSidebarBound !== 'true') {
      this.sidebarCloseButton.dataset.layersSidebarBound = 'true';
      this.sidebarCloseButton.addEventListener('click', (event) => {
        event.preventDefault();
        this.setSidebarCollapsed(true);
      });
    }
  }

  setSidebarCollapsed(collapsed) {
    if (!this.sidebarPanel) return;
    this.sidebarPanel.classList.toggle('is-collapsed', collapsed);
    this.sidebarOpenButtons.forEach((button) => {
      button.setAttribute('aria-expanded', collapsed ? 'false' : 'true');
    });
  }

  applyFormToUrl({ resetPage }) {
    const params = getUrlParams();

    Array.from(params.keys()).forEach((key) => {
      if (isFilterParam(key) || key === 'sort' || key === 'sort_by') {
        params.delete(key);
      }
    });
    if (resetPage) params.delete('page');

    const seen = new Set();
    const inputs = this.section.querySelectorAll(
      '[data-layers-filters-form] input[name], [data-layers-filters-form] select[name]'
    );

    inputs.forEach((input) => {
      const name = input.name;
      if (!name) return;

      if (input.matches('[data-layers-sort], select[data-layers-sort], select[name="sort"], select[name="sort_by"]')) {
        const value = stripWrappingQuotes(input.value);
        if (value) params.set('sort', value);
        return;
      }

      if (!isFilterParam(name)) return;
      if (input.disabled) return;

      const type = String(input.type || '').toLowerCase();
      if ((type === 'checkbox' || type === 'radio') && !input.checked) return;

      const value = stripWrappingQuotes(input.value);
      if (value === '') return;

      const signature = `${name}::${value}`;
      if (seen.has(signature)) return;
      seen.add(signature);
      params.append(name, value);
    });

    setHistoryParams(params);
    this.refresh();
  }

  clearFilters() {
    const params = getUrlParams();
    Array.from(params.keys()).forEach((key) => {
      if (isFilterParam(key)) params.delete(key);
    });
    params.delete('page');
    setHistoryParams(params);
    this.refresh();
  }

  removeSingleFilterValue(key, value) {
    if (!key || !value) return;
    const params = getUrlParams();
    const targetName = `filters[${key}]`;
    const existingValues = params.getAll(targetName);
    params.delete(targetName);
    existingValues.forEach((entry) => {
      if (stripWrappingQuotes(entry) !== stripWrappingQuotes(value)) {
        params.append(targetName, entry);
      }
    });
    params.delete('page');
    setHistoryParams(params);
    this.refresh();
  }

  removeRangeFilter(key) {
    if (!key) return;
    const params = getUrlParams();
    params.delete(`filters[${key}][min]`);
    params.delete(`filters[${key}][max]`);
    params.delete('page');
    setHistoryParams(params);
    this.refresh();
  }

  setPage(page) {
    const params = getUrlParams();
    if (page > 1) params.set('page', String(page));
    else params.delete('page');
    setHistoryParams(params);
    this.refresh();
    const top = this.grid.getBoundingClientRect().top + window.pageYOffset - 120;
    window.scrollTo({ top: Math.max(0, top), behavior: 'smooth' });
  }

  getCurrentSortCode() {
    const selected = getSortFromParams();
    if (selected) return selected;
    if (this.defaultSort) return this.defaultSort;
    return getLayersConfig().defaultSortOrder || 'best_selling';
  }

  updateSortUI() {
    const selects = this.section.querySelectorAll('[data-layers-sort]');
    if (!selects.length) return;

    const sorts = filterSortsByScope(
      normalizeSorts(getLayersConfig().sortOrders || []),
      ['collection', 'browse']
    );

    const selectedCode = this.getCurrentSortCode();
    const sortPrefix = 'Sort By: ';

    selects.forEach((select) => {
      const previousValue = selectedCode || select.value || '';
      const options = [];
      sorts.forEach((sort) => {
        const selectedAttr = sort.code === previousValue ? ' selected' : '';
        options.push(
          `<option value="${escapeAttr(sort.code)}"${selectedAttr}>${escapeHtml(`${sortPrefix}${sort.label}`)}</option>`
        );
      });
      select.innerHTML = options.join('');
      if (previousValue) {
        select.value = previousValue;
      }
      if (!select.value && sorts.some((sort) => sort.code === 'best_selling')) {
        select.value = 'best_selling';
      }
      if (!select.value && sorts[0]?.code) {
        select.value = sorts[0].code;
      }
    });
  }

  updateFiltersUI({ facetsData, facetRanges }) {
    const facetLabelMap = getFacetLabelMap();
    const configuredFacetOrder = (getLayersConfig().facets || []).map((facet) => facet?.code).filter(Boolean);
    const selectedValues = getSelectedFacetValues();
    const selectedRanges = getSelectedRangeValues();

    const keys = [];
    configuredFacetOrder.forEach((key) => keys.push(key));
    Object.keys(facetsData || {}).forEach((key) => {
      if (!keys.includes(key)) keys.push(key);
    });
    Object.keys(facetRanges || {}).forEach((key) => {
      if (!keys.includes(key)) keys.push(key);
    });

    const buildMarkup = (idPrefix) => {
      const details = [];
      keys.forEach((facetKey, index) => {
        const title = facetLabelMap[facetKey] || facetKey;
        const range = parseFacetRange(facetRanges?.[facetKey]);
        const values = facetsData?.[facetKey] || {};

        const selectedSet = selectedValues.get(facetKey) || new Set();
        if (selectedSet.size) {
          selectedSet.forEach((value) => {
            if (values[value] == null) values[value] = 0;
          });
        }

        const valueEntries = Object.entries(values).filter(([value, count]) => {
          const normalized = stripWrappingQuotes(value);
          if (!normalized) return false;
          return Number(count) > 0 || selectedSet.has(normalized);
        });

        if ((!range || range.min === range.max) && !selectedSet.size && valueEntries.length <= 1) return;
        details.push(
          buildFacetDetailsMarkup({
            facetKey,
            title,
            values: valueEntries.reduce((acc, [k, v]) => {
              acc[k] = v;
              return acc;
            }, {}),
            selectedValues: selectedSet,
            selectedRange: selectedRanges.get(facetKey),
            range,
            index,
            idPrefix,
            isColorFacet: isColorFacetKey(facetKey, title),
            isSizeFacet: isSizeFacetKey(facetKey, title),
          })
        );
      });
      return details.join('');
    };

    this.section.querySelectorAll('[data-layers-drawer-filters]').forEach((container, index) => {
      container.innerHTML = buildMarkup(`layers-drawer-${this.dataset.sectionId || 'section'}-${index}`);
    });

    this.section.querySelectorAll('[data-layers-sidebar-filters]').forEach((container, index) => {
      container.innerHTML = buildMarkup(`layers-sidebar-${this.dataset.sectionId || 'section'}-${index}`);
    });

    const activeFacetsContainer = this.section.querySelector('[data-layers-active-facets]');
    if (activeFacetsContainer) {
      const markup = buildActiveFacetsMarkup({
        listSelections: selectedValues,
        rangeSelections: selectedRanges,
        facetLabelMap,
      });
      activeFacetsContainer.innerHTML = markup;
      activeFacetsContainer.toggleAttribute('hidden', markup === '');
    }
  }

  setResultsCount(displayed, total) {
    const countNodes = this.section.querySelectorAll('#ProductCount, .collection__products-count');
    const normalizeValue = (value) => {
      if (value == null) return '';
      const numeric = Number(value);
      if (Number.isFinite(numeric)) return numeric;
      const text = String(value).trim();
      return text;
    };

    const shown = normalizeValue(displayed);
    const overall = normalizeValue(total);

    countNodes.forEach((node) => {
      if (shown === '' && overall === '') {
        node.textContent = '';
        return;
      }

      if (shown !== '' && overall !== '') {
        node.textContent = `Showing ${shown} of ${overall} products`;
        return;
      }

      if (overall !== '') {
        node.textContent = `${overall} products`;
        return;
      }

      node.textContent = `Showing ${shown} products`;
    });
  }

  renderHeroBanners(banners) {
    if (!this.heroBannerContainer) return;

    const heroes = collectBanners(banners)
      .map((banner) => getBannerLayoutForDevice(banner, this.bannerMediaQuery?.matches === true))
      .filter((picked) => picked?.layout?.placement === 'hero')
      .map((picked) => renderBannerCell(picked, 'hero'));

    this.heroBannerContainer.innerHTML = heroes.join('');
    this.heroBannerContainer.toggleAttribute('hidden', heroes.length === 0);
  }

  clearHeroBanners() {
    if (!this.heroBannerContainer) return;
    this.heroBannerContainer.innerHTML = '';
    this.heroBannerContainer.setAttribute('hidden', '');
  }

  renderProducts(products, page, banners = []) {
    if (!this.grid) return;
    this.renderHeroBanners(banners);

    const productCards = buildGridCells({
      products,
      banners,
      isMobile: this.bannerMediaQuery?.matches === true,
    });
    const withPromos = page === 1 ? injectPromos(productCards, this.promos) : productCards;
    this.grid.innerHTML = withPromos.length
      ? withPromos.join('')
      : '<h3 class="collection__title collection__no-result text-center">No products found</h3>';
  }

  showLoadingState() {
    this.setResultsCount('...', '...');

    if (this.paginationContainer) {
      this.paginationContainer.setAttribute('aria-busy', 'true');
    }

    if (this.hasLoadedResults) return;

    if (this.grid && this.gridSkeletonMarkup) {
      this.grid.innerHTML = this.gridSkeletonMarkup;
    }
    if (this.heroBannerContainer && !this.hasLoadedResults) {
      this.heroBannerContainer.innerHTML = '';
      this.heroBannerContainer.setAttribute('hidden', '');
    }

    this.section
      .querySelectorAll('[data-layers-drawer-filters], [data-layers-sidebar-filters]')
      .forEach((container) => {
        const skeleton = this.filterSkeletonByContainer.get(container);
        if (skeleton != null) {
          container.innerHTML = skeleton;
        }
      });

    const activeFacetsContainer = this.section.querySelector('[data-layers-active-facets]');
    if (activeFacetsContainer) {
      activeFacetsContainer.innerHTML = '';
      activeFacetsContainer.setAttribute('hidden', '');
    }

    if (this.paginationContainer) {
      this.paginationContainer.innerHTML = '';
    }
  }

  async refresh() {
    if (!this.controller || !this.handle) return;

    const currentRequest = this.requestToken + 1;
    this.requestToken = currentRequest;

    const params = getUrlParams();
    const page = getPageFromParams(params);
    const { listFilters, rangeFilters } = parseFiltersFromParams(params);
    const filters = buildFilterGroup(listFilters, rangeFilters);
    const sortOrderCode = this.getCurrentSortCode();
    const shouldIncludeFacets = this.filtersMode !== 'none';

    this.updateSortUI();

    const request = this.controller.execute({
      page,
      limit: this.limit,
      filters,
      sortOrderCode: sortOrderCode || undefined,
      includeFilters: shouldIncludeFacets,
      includeFacetRanges: shouldIncludeFacets,
    });
    this.showLoadingState();
    const result = await request;

    if (currentRequest !== this.requestToken) return;
    if (this.paginationContainer) {
      this.paginationContainer.removeAttribute('aria-busy');
    }
    if (result?.error) {
      console.error('[Layers] Collection error', result.error.message);
      if (this.grid) {
        this.grid.innerHTML = '<h3 class="collection__title collection__no-result text-center">Unable to load products</h3>';
      }
      this.clearHeroBanners();
      this.setResultsCount('', '');
      return;
    }

    const products = result?.data?.products || [];
    const totalResults = getTotalResults(result?.data, products.length);
    const totalPages = getTotalPages(result?.data, this.limit, 1);
    const disjunctiveFacets = shouldIncludeFacets
      ? await this.fetchDisjunctiveFacetData({
          listFilters,
          rangeFilters,
          sortOrderCode,
        })
      : {};

    if (currentRequest !== this.requestToken) return;

    const facetData = mergeFacetData(
      result?.data?.facets || {},
      result?.data?.facetRanges || {},
      disjunctiveFacets
    );

    this.renderProducts(products, page, result?.data?.banners || result?.data?.appliedRules || []);
    this.setResultsCount(products.length, totalResults);
    this.updateFiltersUI({
      facetsData: facetData.facets,
      facetRanges: facetData.facetRanges,
    });
    renderPagination(this.paginationContainer, page, totalPages);
    this.hasLoadedResults = true;
  }

  async fetchDisjunctiveFacetData({ listFilters, rangeFilters, sortOrderCode }) {
    const activeFacetKeys = getActiveFacetKeys(listFilters, rangeFilters);
    if (!activeFacetKeys.length) return {};

    const entries = await Promise.all(
      activeFacetKeys.map(async (facetKey) => {
        const remainingFilters = omitFacetFromFilters(listFilters, rangeFilters, facetKey);
        const controller = this.sdk.collection({
          handle: this.handle,
          defaultSort: this.defaultSort || 'best_selling',
        });

        try {
          const result = await controller.execute({
            page: 1,
            limit: 1,
            filters: buildFilterGroup(remainingFilters.listFilters, remainingFilters.rangeFilters),
            sortOrderCode: sortOrderCode || undefined,
            includeFilters: true,
            includeFacetRanges: true,
          });

          if (result?.error) {
            console.error('[Layers] Disjunctive facet error', result.error.message);
            return null;
          }

          return [
            facetKey,
            {
              values: result?.data?.facets?.[facetKey],
              range: result?.data?.facetRanges?.[facetKey],
            },
          ];
        } finally {
          controller?.dispose?.();
        }
      })
    );

    return entries.reduce((acc, entry) => {
      if (!entry) return acc;
      const [key, data] = entry;
      if (data?.values || data?.range) acc[key] = data;
      return acc;
    }, {});
  }
}

class LayersFeaturedProducts extends HTMLElement {
  connectedCallback() {
    this.sdk = getSdk();
    if (!this.sdk) return;
    this.limit = Number(this.dataset.limit) || 8;
    this.section = this.closest('.shopify-section') || this;
    this.gridSkeletonByBlock = new WeakMap();
    this.querySelectorAll('[data-layers-featured-products-block]').forEach((block) => {
      const grid = block.querySelector('[data-layers-featured-products-grid]');
      if (!grid) return;
      this.gridSkeletonByBlock.set(grid, grid.innerHTML);
    });
    this.refresh();
  }

  async refresh() {
    const blocks = this.querySelectorAll('[data-layers-featured-products-block]');
    if (!blocks.length) return;

    await Promise.all(
      Array.from(blocks).map(async (block) => {
        const handle = block.getAttribute('data-collection-handle');
        if (!handle) return;

        const sortOrderCode =
          block.getAttribute('data-sort-order-code') || undefined;
        const grid = block.querySelector('[data-layers-featured-products-grid]');
        if (!grid) return;

        const skeletonMarkup = this.gridSkeletonByBlock.get(grid);
        if (skeletonMarkup != null) {
          grid.innerHTML = skeletonMarkup;
        }

        const customSlide = grid.querySelector('.products-feed__card-text-custom-slide');
        const customSlideMarkup = customSlide ? customSlide.outerHTML : '';

        const controller = this.sdk.collection({
          handle,
          defaultSort: sortOrderCode,
        });

        const result = await controller.execute({
          page: 1,
          limit: this.limit,
          sortOrderCode,
        });

        controller.dispose?.();

        if (result?.error) {
          console.error('[Layers] Featured products error', result.error.message);
          return;
        }

        const products = result?.data?.products || [];
        const cards = products
          .map(
            (product) =>
              `<div class="swiper-slide card-product-slider__slide">${renderGenericProductCard(product)}</div>`
          )
          .join('');

        grid.innerHTML = `${cards}${customSlideMarkup}`;
      })
    );

    requestAnimationFrame(() => {
      document.dispatchEvent(new CustomEvent('theme:resize:width', { bubbles: true }));
    });
  }
}

class LayersFeaturedCollections extends HTMLElement {
  connectedCallback() {
    this.sdk = getSdk();
    if (!this.sdk) return;
    this.refresh();
  }

  async refresh() {
    const slides = this.querySelectorAll('[data-layers-featured-collection-slide]');
    if (!slides.length) return;

    await Promise.all(
      Array.from(slides).map(async (slide) => {
        const handle = slide.getAttribute('data-collection-handle');
        if (!handle) return;
        const collectionUrl = slide.getAttribute('data-collection-url');
        const link = slide.querySelector('[data-layers-featured-collection-link]');
        if (link && collectionUrl) {
          link.setAttribute('href', collectionUrl);
        }

        const sortOrderCode =
          slide.getAttribute('data-sort-order-code') || undefined;
        const hasCustomImage = slide.getAttribute('data-has-custom-image') === 'true';

        const controller = this.sdk.collection({
          handle,
          defaultSort: sortOrderCode,
        });
        const result = await controller.execute({
          page: 1,
          limit: 1,
          sortOrderCode,
          includeFilters: false,
          includeFacetRanges: false,
        });
        controller.dispose?.();

        if (result?.error) {
          console.error('[Layers] Featured collections error', result.error.message);
          return;
        }

        const product = result?.data?.products?.[0];
        const total = getTotalResults(result?.data, 0);

        const countNode = slide.querySelector('.card-collection__counter');
        if (countNode && Number.isFinite(Number(total))) {
          countNode.textContent = String(total);
        }

        if (hasCustomImage) return;
        const media = slide.querySelector('.card-collection__media');
        if (!media || !product?.images?.[0]?.url) return;
        const image = product.images[0];
        media.innerHTML = renderCardImage({ src: image.url, alt: product.title || '' });
      })
    );
  }
}

class LayersRecommendations extends HTMLElement {
  connectedCallback() {
    this.sdk = getSdk();
    this.grid = this.querySelector('[data-layers-grid]');
    if (!this.sdk || !this.grid) return;

    this.blockId = normalizeId(this.dataset.blockId);
    this.anchorId = normalizeId(this.dataset.anchorId);
    this.anchorHandle = String(this.dataset.anchorHandle || '').trim();
    this.limit = Number(this.dataset.limit) || 8;
    this.carousel = this.dataset.carousel === 'true' || this.dataset.carousel === '1';
    this.lazyLoad = this.dataset.lazyLoad === 'true' || this.dataset.lazyLoad === '1';
    this.lazyRootMargin = this.dataset.lazyRootMargin || '200px';
    this.skeletonMarkup = this.grid.innerHTML;
    this.hasSkeleton = this.skeletonMarkup.trim().length > 0;

    if (this.lazyLoad) {
      this.setupLazyLoad();
    } else {
      this.refresh();
    }
  }

  disconnectedCallback() {
    this.lazyObserver?.disconnect();
  }

  setupLazyLoad() {
    if (this.lazyObserver) return;
    if (!('IntersectionObserver' in window)) {
      this.refresh();
      return;
    }
    this.lazyObserver = new IntersectionObserver(
      (entries) => {
        if (!entries.some((entry) => entry.isIntersecting)) return;
        this.lazyObserver.disconnect();
        this.lazyObserver = null;
        this.refresh();
      },
      { rootMargin: this.lazyRootMargin, threshold: 0.01 }
    );
    this.lazyObserver.observe(this);
  }

  showSkeleton() {
    if (!this.grid) return;
    if (!this.hasSkeleton) {
      this.grid.innerHTML = '';
      return;
    }
    this.grid.innerHTML = this.skeletonMarkup;
  }

  getEmptyMarkup(message) {
    if (this.carousel) {
      return `<div class="swiper-slide card-product-slider__slide"><p class="layers-recommendations__empty">${escapeHtml(message)}</p></div>`;
    }
    return `<p class="layers-recommendations__empty">${escapeHtml(message)}</p>`;
  }

  refreshCarousel() {
    if (!this.carousel || !this.grid) return;
    requestAnimationFrame(() => {
      document.dispatchEvent(new CustomEvent('theme:resize:width', { bubbles: true }));
    });
  }

  async refresh() {
    if (!this.grid) return;
    if (!this.blockId) {
      this.grid.innerHTML = this.getEmptyMarkup('Select a Layers block.');
      return;
    }

    this.showSkeleton();

    const controller = this.sdk.blocks({
      blockId: this.blockId,
      anchorId: this.anchorId || undefined,
      anchorHandle: this.anchorHandle || undefined,
    });

    const result = await controller.execute({ limit: this.limit });
    controller.dispose?.();

    if (result?.error) {
      console.error('[Layers] Recommendations error', result.error.message);
      this.grid.innerHTML = this.getEmptyMarkup('Unable to load recommendations.');
      return;
    }

    const products = result?.data?.products || [];
    if (!products.length) {
      this.grid.innerHTML = this.getEmptyMarkup('No recommendations found.');
      return;
    }

    if (this.carousel) {
      this.grid.innerHTML = products
        .map(
          (product) =>
            `<div class="swiper-slide card-product-slider__slide">${renderGenericProductCard(product)}</div>`
        )
        .join('');
    } else {
      this.grid.innerHTML = products.map((product) => renderGenericProductCard(product)).join('');
    }

    this.refreshCarousel();
  }
}

function initLayersRuntime() {
  const sdk = getSdk();
  if (!sdk) return;

  window.layers = window.layers || {};
  window.layers.sdk = sdk;

  if (!customElements.get('layers-collection-results')) {
    customElements.define('layers-collection-results', LayersCollectionResults);
  }
  if (!customElements.get('layers-featured-products')) {
    customElements.define('layers-featured-products', LayersFeaturedProducts);
  }
  if (!customElements.get('layers-featured-collections')) {
    customElements.define('layers-featured-collections', LayersFeaturedCollections);
  }
  if (!customElements.get('layers-recommendations')) {
    customElements.define('layers-recommendations', LayersRecommendations);
  }

  document.dispatchEvent(
    new CustomEvent('layers:sdk:ready', {
      bubbles: true,
      detail: { sdk },
    })
  );
}

initLayersRuntime();
